/**
 * Message Notification Utilities
 * Handles sound playback and browser notifications for new messages
 */

/**
 * Request notification permission from the user
 */
export const requestNotificationPermission = async (): Promise<boolean> => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

/**
 * Play a notification sound
 * Uses Web Audio API to generate a simple notification sound
 */
export const playNotificationSound = () => {
  try {
    // Create audio context
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    // Create oscillator for the sound
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    // Connect nodes
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Configure sound (gentle notification beep)
    oscillator.frequency.value = 800; // Frequency in Hz
    oscillator.type = 'sine';
    
    // Configure volume envelope (fade in/out)
    const now = audioContext.currentTime;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
    
    // Play sound
    oscillator.start(now);
    oscillator.stop(now + 0.3);
  } catch (error) {
    console.error('Error playing notification sound:', error);
    // Fallback: try using HTML5 audio if available
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjGH0fPTgjMGHm7A7+OZURE');
      audio.volume = 0.3;
      audio.play().catch(() => {
        // Ignore errors if audio can't play
      });
    } catch (fallbackError) {
      // Silently fail if sound can't be played
    }
  }
};

/**
 * Show a browser notification for a new message
 */
export const showMessageNotification = async (
  senderName: string,
  messageText: string,
  conversationName?: string
): Promise<void> => {
  const hasPermission = await requestNotificationPermission();
  
  if (!hasPermission) {
    return;
  }

  // Truncate message text if too long
  const truncatedText = messageText.length > 100 
    ? messageText.substring(0, 100) + '...' 
    : messageText;

  const title = conversationName 
    ? `${senderName} in ${conversationName}`
    : senderName;

  try {
    const notification = new Notification(title, {
      body: truncatedText,
      icon: '/favicon.svg', // Use SVG favicon for notifications
      badge: '/favicon.svg', // Use SVG favicon for badge (fallback if 32x32 not found)
      tag: 'message-notification', // Group notifications
      requireInteraction: false,
    });

    // Auto-close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);

    // Handle click - focus the window
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch (error) {
    console.error('Error showing notification:', error);
  }
};

/**
 * Check if the current tab/window is focused
 */
export const isWindowFocused = (): boolean => {
  return document.hasFocus();
};

/**
 * Update browser tab title to show badge/alert for new messages
 */
let originalTitle = '';
let badgeCount = 0;
let isInitialized = false;

export const updateBrowserTabBadge = (newMessageCount: number = 1): void => {
  if (!isInitialized) {
    initBrowserTabBadge();
  }
  
  badgeCount += newMessageCount;
  
  // Update tab title with badge count
  if (badgeCount > 0) {
    // Use originalTitle if it's set, otherwise clean the current title
    const cleanTitle = originalTitle || document.title.replace(/^\(\d+\)\s+/, '');
    
    // Add new badge
    const newTitle = `(${badgeCount}) ${cleanTitle}`;
    document.title = newTitle;
    console.log('📌 Tab badge updated:', newTitle, 'badgeCount:', badgeCount);
  } else {
    // Reset to original title
    document.title = originalTitle || 'Complin.ai - Compliance Management';
  }
};

/**
 * Clear browser tab badge
 */
export const clearBrowserTabBadge = (): void => {
  badgeCount = 0;
  const titleToUse = originalTitle || 'Complin.ai - Compliance Management';
  document.title = titleToUse;
  console.log('📌 Tab badge cleared, title reset to:', titleToUse);
};

/**
 * Initialize browser tab badge (should be called on app load)
 */
export const initBrowserTabBadge = (): void => {
  if (!isInitialized) {
    // Store the original title without any badge
    const currentTitle = document.title;
    originalTitle = currentTitle.replace(/^\(\d+\)\s+/, '') || currentTitle || 'Complin.ai - Compliance Management';
    isInitialized = true;
    console.log('📌 Tab badge initialized with original title:', originalTitle);
  }
};

/**
 * Play notification sound (always plays, regardless of focus state)
 */
export const playNotificationSoundAlways = (): void => {
  playNotificationSound();
};

